import { environment } from '@jepto/environments/environment';
import {
	checkCurrentDate,
	checkFutureDate,
	getEpoch,
} from '@jepto/shared/utils/date';
import { insertString } from '@jepto/shared/utils/regex';
import { camelCase, compact, filter, includes, isEmpty, pickBy } from 'lodash';

import * as CryptoJS from 'crypto-js';
import { withJeptoLegacyChartSurface } from '@jepto/shared/utils/jepto-legacy-chart-surface.util';
import {
	onLegacyChartLoad,
	onLegacyChartRender,
} from '@jepto/shared/utils/legacy-chart-reflow.util';

import * as Highcharts from 'highcharts';
import Masonry from 'masonry-layout';

import { download, generateCsv, mkConfig } from 'export-to-csv';

import dayjs from '@jepto/config/dayjs.config';
import { log } from '@jepto/shared/utils/logger';
import * as Sentry from '@sentry/angular';
import { getIanaTimezoneLocationList } from '@framework/utils/iana-timezone-location-list';

declare var FS: any;
window['_fs_namespace'] = 'FS';

export const encrypt: Function = (message: string, key: string): any => {
	return CryptoJS.AES.encrypt(message, key).toString();
};

export const decrypt: Function = (encrypted: string, key: string): string => {
	return CryptoJS.AES.decrypt(encrypted, key).toString(CryptoJS.enc.Utf8);
};

export const hash512: Function = (text: string): string => {
	return CryptoJS.SHA512(text).toString();
};

export const isErrorExistingFromGQLResponse: Function = (
	gqlResponse: any
): boolean => {
	const isErrorExisting: number = getKeyFromObject(gqlResponse, 'errors');
	return isErrorExisting === -1 ? false : true;
};

export const getKeyFromObject: Function = (
	collections: Object,
	key: string
): number => {
	if (collections instanceof Object === true) {
		const togetKeyInstance: number = Object.keys(collections).indexOf(key);
		return togetKeyInstance;
	} else {
		return;
	}
};

export const sortKeySplitter: Function = (
	sortKey: string,
	seperator: string,
	position?: number
): any => {
	if (Boolean(sortKey) === true) {
		let collections: Array<any> = sortKey.split(seperator);
		return position >= 0 ? collections[position] : collections;
	}
};

/**
 * Prefer explicit `id`; otherwise the last `#` segment of a composite AppSync `sortKey`.
 * Use instead of `entity?.sortKey.split(...)` — optional chaining on `entity` does not
 * protect `.split` when `sortKey` is undefined (production TypeError).
 */
export function clientIdFromIdOrSortKeyTail(entity: {
	id?: string | null;
	sortKey?: string | null;
}): string | undefined {
	if (entity?.id != null && String(entity.id).length > 0) {
		return String(entity.id);
	}
	const sk = entity?.sortKey;
	if (sk == null || typeof sk !== 'string' || sk.length === 0) {
		return undefined;
	}
	const parts = sk.split('#');
	return parts.length > 0 ? parts[parts.length - 1] : undefined;
}

/**
 * Composite sort key for budget comments / `commentTo` on comment mutations (AppSync).
 * Prefer `clientId` and `id`; otherwise derive from a full `budget#v0#…` `sortKey`.
 */
export function getBudgetCommentToKey(budgetDetails: {
	clientId?: string;
	id?: string;
	sortKey?: string;
	client?: { id: string };
}): string {
	const sk = budgetDetails.sortKey?.trim();
	// Detail/list APIs often already return the canonical composite key — use as-is (same as global "all clients" flow).
	if (sk?.startsWith('budget#v0#')) {
		return sk;
	}
	const rawId = budgetDetails.id?.trim();
	// Some payloads put the full composite in `id`; do not wrap again or AppSync returns validation errors and listComments shows nothing.
	if (rawId?.startsWith('budget#v0#')) {
		return rawId;
	}
	const clientId: string =
		budgetDetails.clientId ||
		budgetDetails.client?.id ||
		sortKeySplitter(sk, '#', 2);
	const budgetId: string = rawId || sortKeySplitter(sk, '#', 3);
	return `budget#v0#${clientId}#${budgetId}`;
}

export const getFormattedXYDataObject: Function = (
	date: string,
	y: string
): { x: number; y: number } => {
	return { x: getEpoch(date), y: parseFloat(y) };
};

// export const getFormattedDataObject: Function = (date: string, y: string): { x: number, y: number } => {
// 	return [getEpoch(date), parseFloat(y)]
// };

export const dynamicKeySort: Function = (sortKey: string): any => {
	let sortIndexOrder: number = 1;
	if (sortKey[0] === '-') {
		sortIndexOrder = -1;
		sortKey = sortKey.substr(1);
	}
	return (inner: any, outer: any) => {
		let innerValue = inner[sortKey];
		let outerValue = outer[sortKey];
		if (typeof innerValue === 'string')
			innerValue = ('' + innerValue).toLowerCase();
		if (typeof outerValue === 'string')
			outerValue = ('' + outerValue).toLowerCase();
		let num: number =
			innerValue < outerValue ? -1 : innerValue > outerValue ? 1 : 0;
		return num * sortIndexOrder;
	};
};

export const createSortValuesOnStatus: Function = (
	jobStatus: string
): string => {
	if (jobStatus === 'ERROR') {
		return 'a';
	} else if (jobStatus === 'CRITICAL') {
		return 'b';
	} else if (jobStatus === 'WARNING') {
		return 'c';
	} else if (jobStatus === 'ON_TRACK') {
		return 'd';
	}
};

export const assignValueOnObjectCollections: Function = (
	items: Object,
	value: any
): boolean => {
	Object.assign(items, value);
	return true;
};

export const paginateArray: Function = (
	items: Array<any>,
	pageSize: number,
	pageNumber: number
): Array<any> => {
	--pageNumber; // because pages logically start with 1, but technically with 0
	log('clg paginateArray', items, pageSize, pageNumber);
	log(
		'clg paginateArray page items',
		items.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize)
	);
	return items.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize);
};

export const runFullStoryIdentify: Function = (
	id: string,
	userDetails: Object,
	anonimised: false
): boolean => {
	anonimised ? FS.identify(false) : FS.identify(id, userDetails);
	return true;
};

export const capitalise: Function = (
	value: string,
	toUpper: boolean
): string => {
	if (Boolean(value)) {
		const halfString: string = value.slice(1).toLowerCase();
		let firstLetter: string = value.charAt(0);
		const newValue: string =
			toUpper === true
				? `${firstLetter.toUpperCase()}${halfString}`
				: `${firstLetter}${halfString}`;
		return newValue;
	} else {
		return '';
	}
};

export const removeURLparameterOnString: Function = (
	url: string,
	removeAllParemeters: boolean,
	parameter?: string
): string => {
	const urlparts = url.split('?');
	if (removeAllParemeters === true) {
		return urlparts[0];
	} else {
		if (urlparts.length >= 2) {
			const prefix = encodeURIComponent(parameter) + '=';
			const pars = urlparts[1].split(/[&;]/g);
			for (let i = pars.length; i-- > 0; ) {
				if (pars[i].lastIndexOf(prefix, 0) !== -1) {
					pars.splice(i, 1);
				}
			}
			return urlparts[0] + (pars.length > 0 ? '?' + pars.join('&') : '');
		}
	}
};

export const memoize: Function = (func: Function): any => {
	const cache = {};
	return function () {
		const key = JSON.stringify(arguments);
		if (cache[key]) {
			return cache[key];
		} else {
			const val = func.apply(this, arguments);
			cache[key] = val;
			return val;
		}
	};
};

export const getValueOnObjectKey: Function = (item: Object): any => {
	let toValue: string = 'None';
	let toKey = Object.keys(item).pop();
	Boolean(toKey) === true
		? (toValue = item[`${toKey}`])
		: (toValue = toValue);
	return toValue;
};

export const differenceWith: Function = (
	array1: Array<any>,
	array2: Array<any>,
	key: string
): Array<any> => {
	if (Array.isArray(array1) === true && Array.isArray(array2) === true) {
		return array1.filter(
			o1 => array2.filter(o2 => o2[key] === o1[key]).length === 0
		);
	} else {
		throw new Error('Type is not array');
	}
};

export const getLocationList: Function = (): Array<any> => {
	return [...getIanaTimezoneLocationList()] as Array<any>;
};

export const getTimeZoneLocationRaw: Function = (): Array<any> => {
	return [
		'Africa/Abidjan',
		'Africa/Accra',
		'Africa/Addis_Ababa',
		'Africa/Algiers',
		'Africa/Asmara',
		'Africa/Asmera',
		'Africa/Bamako',
		'Africa/Bangui',
		'Africa/Banjul',
		'Africa/Bissau',
		'Africa/Blantyre',
		'Africa/Brazzaville',
		'Africa/Bujumbura',
		'Africa/Cairo',
		'Africa/Casablanca',
		'Africa/Ceuta',
		'Africa/Conakry',
		'Africa/Dakar',
		'Africa/Dar_es_Salaam',
		'Africa/Djibouti',
		'Africa/Douala',
		'Africa/El_Aaiun',
		'Africa/Freetown',
		'Africa/Gaborone',
		'Africa/Harare',
		'Africa/Johannesburg',
		'Africa/Juba',
		'Africa/Kampala',
		'Africa/Khartoum',
		'Africa/Kigali',
		'Africa/Kinshasa',
		'Africa/Lagos',
		'Africa/Libreville',
		'Africa/Lome',
		'Africa/Luanda',
		'Africa/Lubumbashi',
		'Africa/Lusaka',
		'Africa/Malabo',
		'Africa/Maputo',
		'Africa/Maseru',
		'Africa/Mbabane',
		'Africa/Mogadishu',
		'Africa/Monrovia',
		'Africa/Nairobi',
		'Africa/Ndjamena',
		'Africa/Niamey',
		'Africa/Nouakchott',
		'Africa/Ouagadougou',
		'Africa/Porto-Novo',
		'Africa/Sao_Tome',
		'Africa/Timbuktu',
		'Africa/Tripoli',
		'Africa/Tunis',
		'Africa/Windhoek',
		'America/Adak',
		'America/Anchorage',
		'America/Anguilla',
		'America/Antigua',
		'America/Araguaina',
		'America/Argentina/Buenos_Aires',
		'America/Argentina/Catamarca',
		'America/Argentina/ComodRivadavia',
		'America/Argentina/Cordoba',
		'America/Argentina/Jujuy',
		'America/Argentina/La_Rioja',
		'America/Argentina/Mendoza',
		'America/Argentina/Rio_Gallegos',
		'America/Argentina/Salta',
		'America/Argentina/San_Juan',
		'America/Argentina/San_Luis',
		'America/Argentina/Tucuman',
		'America/Argentina/Ushuaia',
		'America/Aruba',
		'America/Asuncion',
		'America/Atikokan',
		'America/Atka',
		'America/Bahia',
		'America/Bahia_Banderas',
		'America/Barbados',
		'America/Belem',
		'America/Belize',
		'America/Blanc-Sablon',
		'America/Boa_Vista',
		'America/Bogota',
		'America/Boise',
		'America/Buenos_Aires',
		'America/Cambridge_Bay',
		'America/Campo_Grande',
		'America/Cancun',
		'America/Caracas',
		'America/Catamarca',
		'America/Cayenne',
		'America/Cayman',
		'America/Chicago',
		'America/Chihuahua',
		'America/Coral_Harbour',
		'America/Cordoba',
		'America/Costa_Rica',
		'America/Creston',
		'America/Cuiaba',
		'America/Curacao',
		'America/Danmarkshavn',
		'America/Dawson',
		'America/Dawson_Creek',
		'America/Denver',
		'America/Detroit',
		'America/Dominica',
		'America/Edmonton',
		'America/Eirunepe',
		'America/El_Salvador',
		'America/Ensenada',
		'America/Fort_Nelson',
		'America/Fort_Wayne',
		'America/Fortaleza',
		'America/Glace_Bay',
		'America/Godthab',
		'America/Goose_Bay',
		'America/Grand_Turk',
		'America/Grenada',
		'America/Guadeloupe',
		'America/Guatemala',
		'America/Guayaquil',
		'America/Guyana',
		'America/Halifax',
		'America/Havana',
		'America/Hermosillo',
		'America/Indiana/Indianapolis',
		'America/Indiana/Knox',
		'America/Indiana/Marengo',
		'America/Indiana/Petersburg',
		'America/Indiana/Tell_City',
		'America/Indiana/Vevay',
		'America/Indiana/Vincennes',
		'America/Indiana/Winamac',
		'America/Indianapolis',
		'America/Inuvik',
		'America/Iqaluit',
		'America/Jamaica',
		'America/Jujuy',
		'America/Juneau',
		'America/Kentucky/Louisville',
		'America/Kentucky/Monticello',
		'America/Knox_IN',
		'America/Kralendijk',
		'America/La_Paz',
		'America/Lima',
		'America/Los_Angeles',
		'America/Louisville',
		'America/Lower_Princes',
		'America/Maceio',
		'America/Managua',
		'America/Manaus',
		'America/Marigot',
		'America/Martinique',
		'America/Matamoros',
		'America/Mazatlan',
		'America/Mendoza',
		'America/Menominee',
		'America/Merida',
		'America/Metlakatla',
		'America/Mexico_City',
		'America/Miquelon',
		'America/Moncton',
		'America/Monterrey',
		'America/Montevideo',
		'America/Montreal',
		'America/Montserrat',
		'America/Nassau',
		'America/New_York',
		'America/Nipigon',
		'America/Nome',
		'America/Noronha',
		'America/North_Dakota/Beulah',
		'America/North_Dakota/Center',
		'America/North_Dakota/New_Salem',
		'America/Ojinaga',
		'America/Panama',
		'America/Pangnirtung',
		'America/Paramaribo',
		'America/Phoenix',
		'America/Port-au-Prince',
		'America/Port_of_Spain',
		'America/Porto_Acre',
		'America/Porto_Velho',
		'America/Puerto_Rico',
		'America/Punta_Arenas',
		'America/Rainy_River',
		'America/Rankin_Inlet',
		'America/Recife',
		'America/Regina',
		'America/Resolute',
		'America/Rio_Branco',
		'America/Rosario',
		'America/Santa_Isabel',
		'America/Santarem',
		'America/Santiago',
		'America/Santo_Domingo',
		'America/Sao_Paulo',
		'America/Scoresbysund',
		'America/Shiprock',
		'America/Sitka',
		'America/St_Barthelemy',
		'America/St_Johns',
		'America/St_Kitts',
		'America/St_Lucia',
		'America/St_Thomas',
		'America/St_Vincent',
		'America/Swift_Current',
		'America/Tegucigalpa',
		'America/Thule',
		'America/Thunder_Bay',
		'America/Tijuana',
		'America/Toronto',
		'America/Tortola',
		'America/Vancouver',
		'America/Virgin',
		'America/Whitehorse',
		'America/Winnipeg',
		'America/Yakutat',
		'America/Yellowknife',
		'Antarctica/Casey',
		'Antarctica/Davis',
		'Antarctica/DumontDUrville',
		'Antarctica/Macquarie',
		'Antarctica/Mawson',
		'Antarctica/McMurdo',
		'Antarctica/Palmer',
		'Antarctica/Rothera',
		'Antarctica/South_Pole',
		'Antarctica/Syowa',
		'Antarctica/Troll',
		'Antarctica/Vostok',
		'Arctic/Longyearbyen',
		'Asia/Aden',
		'Asia/Almaty',
		'Asia/Amman',
		'Asia/Anadyr',
		'Asia/Aqtau',
		'Asia/Aqtobe',
		'Asia/Ashgabat',
		'Asia/Ashkhabad',
		'Asia/Atyrau',
		'Asia/Baghdad',
		'Asia/Bahrain',
		'Asia/Baku',
		'Asia/Bangkok',
		'Asia/Barnaul',
		'Asia/Beirut',
		'Asia/Bishkek',
		'Asia/Brunei',
		'Asia/Calcutta',
		'Asia/Chita',
		'Asia/Choibalsan',
		'Asia/Chongqing',
		'Asia/Chungking',
		'Asia/Colombo',
		'Asia/Dacca',
		'Asia/Damascus',
		'Asia/Dhaka',
		'Asia/Dili',
		'Asia/Dubai',
		'Asia/Dushanbe',
		'Asia/Famagusta',
		'Asia/Gaza',
		'Asia/Harbin',
		'Asia/Hebron',
		'Asia/Ho_Chi_Minh',
		'Asia/Hong_Kong',
		'Asia/Hovd',
		'Asia/Irkutsk',
		'Asia/Istanbul',
		'Asia/Jakarta',
		'Asia/Jayapura',
		'Asia/Jerusalem',
		'Asia/Kabul',
		'Asia/Kamchatka',
		'Asia/Karachi',
		'Asia/Kashgar',
		'Asia/Kathmandu',
		'Asia/Katmandu',
		'Asia/Khandyga',
		'Asia/Kolkata',
		'Asia/Krasnoyarsk',
		'Asia/Kuala_Lumpur',
		'Asia/Kuching',
		'Asia/Kuwait',
		'Asia/Macao',
		'Asia/Macau',
		'Asia/Magadan',
		'Asia/Makassar',
		'Asia/Manila',
		'Asia/Muscat',
		'Asia/Nicosia',
		'Asia/Novokuznetsk',
		'Asia/Novosibirsk',
		'Asia/Omsk',
		'Asia/Oral',
		'Asia/Phnom_Penh',
		'Asia/Pontianak',
		'Asia/Pyongyang',
		'Asia/Qatar',
		'Asia/Qyzylorda',
		'Asia/Rangoon',
		'Asia/Riyadh',
		'Asia/Saigon',
		'Asia/Sakhalin',
		'Asia/Samarkand',
		'Asia/Seoul',
		'Asia/Shanghai',
		'Asia/Singapore',
		'Asia/Srednekolymsk',
		'Asia/Taipei',
		'Asia/Tashkent',
		'Asia/Tbilisi',
		'Asia/Tehran',
		'Asia/Tel_Aviv',
		'Asia/Thimbu',
		'Asia/Thimphu',
		'Asia/Tokyo',
		'Asia/Tomsk',
		'Asia/Ujung_Pandang',
		'Asia/Ulaanbaatar',
		'Asia/Ulan_Bator',
		'Asia/Urumqi',
		'Asia/Ust-Nera',
		'Asia/Vientiane',
		'Asia/Vladivostok',
		'Asia/Yakutsk',
		'Asia/Yangon',
		'Asia/Yekaterinburg',
		'Asia/Yerevan',
		'Atlantic/Azores',
		'Atlantic/Bermuda',
		'Atlantic/Canary',
		'Atlantic/Cape_Verde',
		'Atlantic/Faeroe',
		'Atlantic/Faroe',
		'Atlantic/Jan_Mayen',
		'Atlantic/Madeira',
		'Atlantic/Reykjavik',
		'Atlantic/South_Georgia',
		'Atlantic/St_Helena',
		'Atlantic/Stanley',
		'Australia/ACT',
		'Australia/Adelaide',
		'Australia/Brisbane',
		'Australia/Broken_Hill',
		'Australia/Canberra',
		'Australia/Currie',
		'Australia/Darwin',
		'Australia/Eucla',
		'Australia/Hobart',
		'Australia/LHI',
		'Australia/Lindeman',
		'Australia/Lord_Howe',
		'Australia/Melbourne',
		'Australia/NSW',
		'Australia/North',
		'Australia/Perth',
		'Australia/Queensland',
		'Australia/South',
		'Australia/Sydney',
		'Australia/Tasmania',
		'Australia/Victoria',
		'Australia/West',
		'Australia/Yancowinna',
		'Brazil/Acre',
		'Brazil/DeNoronha',
		'Brazil/East',
		'Brazil/West',
		'CET',
		'CST6CDT',
		'Canada/Atlantic',
		'Canada/Central',
		'Canada/Eastern',
		'Canada/Mountain',
		'Canada/Newfoundland',
		'Canada/Pacific',
		'Canada/Saskatchewan',
		'Canada/Yukon',
		'Chile/Continental',
		'Chile/EasterIsland',
		'Cuba',
		'EET',
		'EST',
		'EST5EDT',
		'Egypt',
		'Eire',
		'Etc/GMT',
		'Etc/GMT+0',
		'Etc/GMT+1',
		'Etc/GMT+10',
		'Etc/GMT+11',
		'Etc/GMT+12',
		'Etc/GMT+2',
		'Etc/GMT+3',
		'Etc/GMT+4',
		'Etc/GMT+5',
		'Etc/GMT+6',
		'Etc/GMT+7',
		'Etc/GMT+8',
		'Etc/GMT+9',
		'Etc/GMT-0',
		'Etc/GMT-1',
		'Etc/GMT-10',
		'Etc/GMT-11',
		'Etc/GMT-12',
		'Etc/GMT-13',
		'Etc/GMT-14',
		'Etc/GMT-2',
		'Etc/GMT-3',
		'Etc/GMT-4',
		'Etc/GMT-5',
		'Etc/GMT-6',
		'Etc/GMT-7',
		'Etc/GMT-8',
		'Etc/GMT-9',
		'Etc/GMT0',
		'Etc/Greenwich',
		'Etc/UCT',
		'Etc/UTC',
		'Etc/Universal',
		'Etc/Zulu',
		'Europe/Amsterdam',
		'Europe/Andorra',
		'Europe/Astrakhan',
		'Europe/Athens',
		'Europe/Belfast',
		'Europe/Belgrade',
		'Europe/Berlin',
		'Europe/Bratislava',
		'Europe/Brussels',
		'Europe/Bucharest',
		'Europe/Budapest',
		'Europe/Busingen',
		'Europe/Chisinau',
		'Europe/Copenhagen',
		'Europe/Dublin',
		'Europe/Gibraltar',
		'Europe/Guernsey',
		'Europe/Helsinki',
		'Europe/Isle_of_Man',
		'Europe/Istanbul',
		'Europe/Jersey',
		'Europe/Kaliningrad',
		'Europe/Kiev',
		'Europe/Kirov',
		'Europe/Lisbon',
		'Europe/Ljubljana',
		'Europe/London',
		'Europe/Luxembourg',
		'Europe/Madrid',
		'Europe/Malta',
		'Europe/Mariehamn',
		'Europe/Minsk',
		'Europe/Monaco',
		'Europe/Moscow',
		'Europe/Nicosia',
		'Europe/Oslo',
		'Europe/Paris',
		'Europe/Podgorica',
		'Europe/Prague',
		'Europe/Riga',
		'Europe/Rome',
		'Europe/Samara',
		'Europe/San_Marino',
		'Europe/Sarajevo',
		'Europe/Saratov',
		'Europe/Simferopol',
		'Europe/Skopje',
		'Europe/Sofia',
		'Europe/Stockholm',
		'Europe/Tallinn',
		'Europe/Tirane',
		'Europe/Tiraspol',
		'Europe/Ulyanovsk',
		'Europe/Uzhgorod',
		'Europe/Vaduz',
		'Europe/Vatican',
		'Europe/Vienna',
		'Europe/Vilnius',
		'Europe/Volgograd',
		'Europe/Warsaw',
		'Europe/Zagreb',
		'Europe/Zaporozhye',
		'Europe/Zurich',
		'GB',
		'GB-Eire',
		'GMT',
		'GMT+0',
		'GMT-0',
		'GMT0',
		'Greenwich',
		'HST',
		'Hongkong',
		'Iceland',
		'Indian/Antananarivo',
		'Indian/Chagos',
		'Indian/Christmas',
		'Indian/Cocos',
		'Indian/Comoro',
		'Indian/Kerguelen',
		'Indian/Mahe',
		'Indian/Maldives',
		'Indian/Mauritius',
		'Indian/Mayotte',
		'Indian/Reunion',
		'Iran',
		'Israel',
		'Jamaica',
		'Japan',
		'Kwajalein',
		'Libya',
		'MET',
		'MST',
		'MST7MDT',
		'Mexico/BajaNorte',
		'Mexico/BajaSur',
		'Mexico/General',
		'NZ',
		'NZ-CHAT',
		'Navajo',
		'PRC',
		'PST8PDT',
		'Pacific/Apia',
		'Pacific/Auckland',
		'Pacific/Bougainville',
		'Pacific/Chatham',
		'Pacific/Chuuk',
		'Pacific/Easter',
		'Pacific/Efate',
		'Pacific/Enderbury',
		'Pacific/Fakaofo',
		'Pacific/Fiji',
		'Pacific/Funafuti',
		'Pacific/Galapagos',
		'Pacific/Gambier',
		'Pacific/Guadalcanal',
		'Pacific/Guam',
		'Pacific/Honolulu',
		'Pacific/Johnston',
		'Pacific/Kiritimati',
		'Pacific/Kosrae',
		'Pacific/Kwajalein',
		'Pacific/Majuro',
		'Pacific/Marquesas',
		'Pacific/Midway',
		'Pacific/Nauru',
		'Pacific/Niue',
		'Pacific/Norfolk',
		'Pacific/Noumea',
		'Pacific/Pago_Pago',
		'Pacific/Palau',
		'Pacific/Pitcairn',
		'Pacific/Pohnpei',
		'Pacific/Ponape',
		'Pacific/Port_Moresby',
		'Pacific/Rarotonga',
		'Pacific/Saipan',
		'Pacific/Samoa',
		'Pacific/Tahiti',
		'Pacific/Tarawa',
		'Pacific/Tongatapu',
		'Pacific/Truk',
		'Pacific/Wake',
		'Pacific/Wallis',
		'Pacific/Yap',
		'Poland',
		'Portugal',
		'ROC',
		'ROK',
		'Singapore',
		'Turkey',
		'UCT',
		'US/Alaska',
		'US/Aleutian',
		'US/Arizona',
		'US/Central',
		'US/East-Indiana',
		'US/Eastern',
		'US/Hawaii',
		'US/Indiana-Starke',
		'US/Michigan',
		'US/Mountain',
		'US/Pacific',
		'US/Samoa',
		'UTC',
		'Universal',
		'W-SU',
		'WET',
		'Zulu',
	];
};

export const searchURLParams: Function = (
	url: string,
	toSearch: string
): string => {
	let tempUrl = new URL(url);
	let result = tempUrl.searchParams.get(toSearch);
	return result;
};

export const sortHistory: Function = (
	history: Array<any>,
	key: any
): Array<any> => {
	if (key.active === 'status') {
		history = [...history].sort(
			dynamicKeySort(
				key.direction === 'desc' ? `-targetMet` : `targetMet`
			)
		);
	} else if (key.active === 'history') {
		history = [...history].sort(
			dynamicKeySort(
				key.direction === 'desc' ? `-startDate` : `startDate`
			)
		);
	} else {
		history = [...history].sort(
			dynamicKeySort(
				key.direction === 'desc' ? `-${key.active}` : key.active
			)
		);
	}
	return history;
};

export const processHistory: Function = (
	history: Array<any>,
	jobStatus: Array<any>
): { selectedHistory; jobStatus } => {
	let selectedHistory = [];
	selectedHistory = Object.keys(history).map(result => {
		let endDate = history[result].endDate;
		if (
			endDate !== undefined &&
			!checkCurrentDate(endDate) &&
			!checkFutureDate(endDate)
		) {
			return history[result];
		}
	});
	selectedHistory = compact(selectedHistory);
	const sortedHistory = [...selectedHistory].sort(
		dynamicKeySort('-startDate')
	);
	const totalCount: number = selectedHistory.length;
	const totalAboveTarget: number = filter(selectedHistory, {
		targetMet: true,
	}).length;
	const totalBelowTarget: number = filter(selectedHistory, {
		targetMet: false,
	}).length;
	jobStatus[0].count = totalCount;
	jobStatus[1].count = totalAboveTarget;
	jobStatus[2].count = totalBelowTarget;
	return { selectedHistory: sortedHistory, jobStatus };
};

export const includeClientName: Function = (
	items: Array<any>,
	clients: Array<any>
): Array<any> => {
	let newItems = [];
	for (let i = 0; i < items.length; i++) {
		const initialId: string = sortKeySplitter(items[i].sortKey, '#', 2);
		let tempClientNames = [];
		for (let j = 0; j < clients.length; j++) {
			const clientId: string = sortKeySplitter(
				clients[j].sortKey,
				'#',
				2
			);
			if (initialId === clientId)
				tempClientNames[initialId] = clients[j].name;
		}
		newItems.push({ ...items[i], clientName: tempClientNames[initialId] });
	}
	return newItems;
};

export const getItemToObjectRecursively: Function = (
	items: Object,
	stash: Array<any>
): Array<any> => {
	for (let item in items) {
		let value = items[item];
		if (value && typeof value === 'object') {
			stash.push({ key: item, value: null }); // Parent key
			getItemToObjectRecursively(value, stash);
		} else {
			stash.push({ key: item, value: value }); // Child key
		}
	}
	return stash;
};

export const getFormattedNotificationSchedule: Function = (
	notification: string,
	timezone: string
): any => {
	log('collection [CollectionUtils] Formatting notification schedule:', {
		notification,
		timezone,
	});

	const weekDaysMap = {
		MON: '2019-06-03',
		TUE: '2019-06-04',
		WED: '2019-06-05',
		THU: '2019-06-06',
		FRI: '2019-06-07',
		SAT: '2019-06-08',
		SUN: '2019-06-09',
	};

	const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
	const frequency: string = sortKeySplitter(notification, '#', 0);
	const newFrequency: string = weekdays.includes(frequency)
		? 'WEEKLY'
		: frequency;
	const utcTime: string = insertString(
		sortKeySplitter(notification, '#', 1),
		2,
		':'
	);

	const now =
		newFrequency === 'WEEKLY'
			? dayjs(weekDaysMap[frequency]).format('YYYY-MM-DD')
			: dayjs().format('YYYY-MM-DD');

	const localTime: string = dayjs
		.utc(`${now} ${utcTime}`)
		.tz(timezone)
		.format('ddd HH:mm a');

	const localTimeCollections: Array<string> = localTime.split(' ');

	log('collection [CollectionUtils] Formatted schedule:', {
		frequency: newFrequency,
		time: localTimeCollections[1],
		on: localTimeCollections[0].toUpperCase(),
		postMeridiem: localTimeCollections[2],
	});

	return pickBy({
		frequency: newFrequency,
		time: localTimeCollections[1],
		_on: localTimeCollections[0].toUpperCase(),
		_post_merdiem: localTimeCollections[2],
	});
};

export const serviceValidationError: Function = (
	feature: 'kpi' | 'budget' | 'anomaly' | 'automation' | 'data-source',
	selectedClient,
	key
): {
	active: Array<{ service: string; key: string }>;
	error: Array<{ service: string; key: string }>;
	allServiceError: boolean;
	selectedClient;
} => {
	const featureServices = {
		'data-source': [
			'googleAds',
			'googleAnalytics',
			'facebookAds',
			'microsoftAds',
			'googleMyBusiness',
			'instagramAds',
			'twitterAds',
			'linkedInAds',
			'tiktokAds',
			'googleAnalytics4',
			'googleSearchConsole',
		],
		kpi: [
			'googleAds',
			'googleAnalytics',
			'facebookAds',
			'microsoftAds',
			'googleMyBusiness',
			'instagramAds',
			'twitterAds',
			'linkedInAds',
			'tiktokAds',
			'googleAnalytics4',
			'googleSearchConsole',
		],
		budget: [
			'googleAds',
			'facebookAds',
			'microsoftAds',
			'tiktokAds',
			'linkedInAds',
			'twitterAds',
			'instagramAds',
		],
		anomaly: [
			'googleAds',
			'googleAnalytics',
			'facebookAds',
			'microsoftAds',
			'googleMyBusiness',
			'instagramAds',
			'twitterAds',
			'linkedInAds',
			'tiktokAds',
			'googleAnalytics4',
			'googleSearchConsole',
		],
		automation: [
			'googleMyBusiness',
			'googleAds',
			'googleAnalytics',
			'googleSearchConsole',
		],
		posts: ['googleMyBusiness'],
	};
	let keys = {
		googleAds: 'gads',
		googleAnalytics: 'ga',
		googleMyBusiness: 'gmb',
		facebookAds: 'fbads',
		googleSearchConsole: 'gsc',
		microsoftAds: 'mads',
		tiktokAds: 'tads',
		linkedInAds: 'lads',
		twitterAds: 'twads',
		instagramAds: 'igads',
		googleAnalytics4: 'ga4',
	};
	const selectedFeatureService: string[] = featureServices[feature];
	let i = 0;
	let connectionStates = {
		active: [],
		error: [],
		allServiceError: false,
		selectedClient: selectedClient,
	};

	// Guard against undefined or null selectedClient[key]
	if (!selectedClient || !selectedClient[key]) {
		return connectionStates;
	}

	do {
		const service = selectedFeatureService[i];
		const connectionStatus = selectedClient[key][service];
		let item = { service: service, key: keys[service] };
		if (connectionStatus !== null && connectionStatus?.status === 'ERROR')
			connectionStates.error.push(item);
		if (connectionStatus !== null && connectionStatus?.status === 'ACTIVE')
			connectionStates.active.push(item);
		i += 1;
		if (i === selectedFeatureService.length) {
			connectionStates.error.length === selectedFeatureService.length
				? (connectionStates.allServiceError = true)
				: (connectionStates.allServiceError = false);
			return connectionStates;
		}
	} while (i < selectedFeatureService.length);
};

export const serviceAvailability: Function = (
	feature:
		| 'kpi'
		| 'budget'
		| 'anomaly'
		| 'automation'
		| 'all'
		| 'local_listings',
	selectedClient
): {
	facebookAds?: 'AVAILABLE' | 'NOT AVAILABLE' | 'ERROR';
	gogleMyBusiness?: 'AVAILABLE' | 'NOT AVAILABLE' | 'ERROR';
	googleAds?: 'AVAILABLE' | 'NOT AVAILABLE' | 'ERROR';
	googleAnalytics?: 'AVAILABLE' | 'NOT AVAILABLE' | 'ERROR';
	googleSearchConsole?: 'AVAILABLE' | 'NOT AVAILABLE' | 'ERROR';
	microsoftAds?: 'AVAILABLE' | 'NOT AVAILABLE' | 'ERROR';
	available: number;
	not_available: number;
	errors: number;
} => {
	const featureServices = {
		kpi: [
			'googleAds',
			'googleAnalytics',
			'facebookAds',
			'microsoftAds',
			'googleMyBusiness',
			'instagramAds',
			'twitterAds',
			'linkedInAds',
			'tiktokAds',
			'googleAnalytics4',
			'googleSearchConsole',
		],
		budget: [
			'googleAds',
			'facebookAds',
			'microsoftAds',
			'tiktokAds',
			'linkedInAds',
			'twitterAds',
			'instagramAds',
		],
		anomaly: [
			'googleAnalytics',
			'googleAnalytics4',
			'googleAds',
			'facebookAds',
			'microsoftAds',
			'googleMyBusiness',
			'googleBusinessProfile',
			'instagramAds',
			'twitterAds',
			'linkedInAds',
			'tiktokAds',
			'googleSearchConsole',
		],
		automation: [
			'googleMyBusiness',
			'googleAds',
			'googleAnalytics',
			'googleSearchConsole',
		],
		all: [
			'gogleMyBusiness',
			'googleAds',
			'googleAnalytics',
			'googleSearchConsole',
			'facebookAds',
		],
		local_listings: ['googleMyBusiness', 'googleBusinessProfile'],
	};
	const selectedFeatureService: string[] = featureServices[feature];
	let i = 0;
	let connectionAvailable = { available: 0, not_available: 0, errors: 0 };
	const dataSources = selectedClient.dataSources.items.reduce((acc, item) => {
		acc[item.service] = item.status;
		return acc;
	}, {});

	log('collection clg dataSources', dataSources);
	do {
		const service = selectedFeatureService[i];
		const connectionStatus = dataSources?.[service];
		log('collection clg connectionStatus', service, connectionStatus);
		const hasConnection = Boolean(connectionStatus);

		if (!hasConnection) {
			connectionAvailable[service] = 'NOT AVAILABLE';
			connectionAvailable.not_available += 1;
		}
		if (hasConnection && connectionStatus === 'ERROR') {
			connectionAvailable[service] = 'ERROR';
			connectionAvailable.errors += 1;
		}
		if (hasConnection && connectionStatus === 'ACTIVE') {
			if (service == 'googleBusinessProfile') {
				connectionAvailable['googleMyBusiness'] = 'AVAILABLE';
				connectionAvailable.available += 1;
			}
			connectionAvailable[service] = 'AVAILABLE';
			connectionAvailable.available += 1;
		}
		i += 1;
		if (i === selectedFeatureService.length) {
			return connectionAvailable;
		}
	} while (i < selectedFeatureService.length);
};

export const removeItemInArray: Function = (
	array: Array<any>,
	itemToRemove: any
): Array<any> => {
	const index: number = array.indexOf(itemToRemove);
	if (index > -1) array.splice(index, 1);
	return array;
};

export const removeItemInArrayObject: Function = (
	array: Array<any>,
	objectKey: string,
	objectValue: string
): Array<any> => {
	const index = array.findIndex(key => key[objectKey] === objectValue);
	array.splice(index, 1);
	return array;
};

export const getCountryHolidays: Function = () => {
	return [
		{ name: 'Argentina', value: 'AR' },
		{ name: 'Austria', value: 'AT' },
		{ name: 'Australia', value: 'AU' },
		{ name: 'Bangladesh', value: 'BD' },
		{ name: 'Belgium', value: 'BE' },
		{ name: 'Brazil', value: 'BR' },
		{ name: 'Belarus', value: 'BY' },
		{ name: 'Canada', value: 'CA' },
		{ name: 'Switzerland', value: 'CH' },
		{ name: 'China', value: 'CH' },
		{ name: 'Colombia', value: 'CO' },
		{ name: 'Czechia', value: 'CZ' },
		{ name: 'Germany', value: 'DE' },
		{ name: 'Denmark', value: 'DK' },
		{ name: 'Egypt', value: 'EG' },
		{ name: 'Spain', value: 'ES' },
		{ name: 'Finland', value: 'FI' },
		{ name: 'France', value: 'FR' },
		{ name: 'United Kingdom', value: 'GB' },
		{ name: 'United States', value: 'US' },
		{ name: 'Croatia', value: 'HR' },
		{ name: 'Hungry', value: 'HU' },
		{ name: 'Indonesia', value: 'ID' },
		{ name: 'Ireland', value: 'IE' },
		{ name: 'India', value: 'IN' },
		{ name: 'Italy', value: 'IT' },
		{ name: 'Japan', value: 'JP' },
		{ name: 'Mexico', value: 'MX' },
		{ name: 'Malaysia', value: 'MY' },
		{ name: 'Netherlands', value: 'NL' },
		{ name: 'Norway', value: 'NO' },
		{ name: 'New Zealand', value: 'NZ' },
		{ name: 'Philippines', value: 'PH' },
		{ name: 'Pakistan', value: 'PK' },
		{ name: 'Poland', value: 'PL' },
		{ name: 'Russia', value: 'RU' },
		{ name: 'Sweden', value: 'SU' },
		{ name: 'Slovenia', value: 'SI' },
		{ name: 'Slovakia', value: 'SK' },
		{ name: 'Thailand', value: 'TH' },
		{ name: 'Turkey', value: 'TR' },
		{ name: 'Ukraine', value: 'US' },
		{ name: 'Vietnam', value: 'VN' },
		{ name: 'South Africa', value: 'ZA' },
	];
};

export const getGCPDatasetLocations: Function = () => {
	return [
		{
			group: 'Americas',
			key: 'us-west4',
			name: 'Las Vegas',
			value: 'us-west4',
		},
		{
			group: 'Americas',
			key: 'us-west2',
			name: 'Los Angeles',
			value: 'us-west2',
		},
		{
			group: 'Americas',
			key: 'northamerica-northeast1',
			name: 'Canada/Montréal',
			value: 'northamerica-northeast1',
		},
		{
			group: 'Americas',
			key: 'us-east4',
			name: 'Northern Virginia',
			value: 'us-east4',
		},
		{
			group: 'Americas',
			key: 'us-west1',
			name: 'Oregon',
			value: 'us-west1',
		},
		{
			group: 'Americas',
			key: 'us-west3',
			name: 'Salt Lake City',
			value: 'us-west3',
		},
		{
			group: 'Americas',
			key: 'southamerica-east1',
			name: 'São Paulo',
			value: 'southamerica-east1',
		},
		{
			group: 'Americas',
			key: 'us-east1',
			name: 'South Carolina',
			value: 'us-east1',
		},
		{
			group: 'Europe',
			key: 'europe-west1',
			name: 'Belgium',
			value: 'europe-west1',
		},
		{
			group: 'Europe',
			key: 'europe-north1',
			name: 'Finland',
			value: 'europe-north1',
		},
		{
			group: 'Europe',
			key: 'europe-west3',
			name: 'Frankfurt',
			value: 'europe-west3',
		},
		{
			group: 'Europe',
			key: 'europe-west2',
			name: 'London',
			value: 'europe-west2',
		},
		{
			group: 'Europe',
			key: 'europe-west4',
			name: 'Netherlands',
			value: 'europe-west4',
		},
		{
			group: 'Europe',
			key: 'europe-west6',
			name: 'Zürich',
			value: 'europe-west6',
		},
		{
			group: 'Asia Pacific',
			key: 'asia-east2',
			name: 'Hongkong',
			value: 'asia-east2',
		},
		{
			group: 'Asia Pacific',
			key: 'asia-southeast2',
			name: 'Indonesia/Jakarta',
			value: 'asia-southeast2',
		},
		{
			group: 'Asia Pacific',
			key: 'asia-south1',
			name: 'India/Mumbai',
			value: 'asia-south1',
		},
		{
			group: 'Asia Pacific',
			key: 'asia-east1',
			name: 'Japan/Tokyo',
			value: 'asia-northeast1',
		},
		{
			group: 'Asia Pacific',
			key: 'asia-northeast2',
			name: 'Japan/Osaka',
			value: 'asia-northeast2',
		},
		{
			group: 'Asia Pacific',
			key: 'asia-northeast3',
			name: 'South Korea/Seoul',
			value: 'asia-northeast3',
		},
		{
			group: 'Asia Pacific',
			key: 'asia-southeast1',
			name: 'Singapore',
			value: 'asia-southeast1',
		},
		{
			group: 'Asia Pacific',
			key: 'australia-southeast1',
			name: 'Australia/Sydney',
			value: 'australia-southeast1',
		},
		{
			group: 'Asia Pacific',
			key: 'asia-east1',
			name: 'Taiwan',
			value: 'asia-east1',
		},
		{
			group: 'Multi-region',
			key: 'EU',
			name: 'Europe',
			value: 'EU',
		},
		{
			group: 'Multi-region',
			key: 'US',
			name: 'United States',
			value: 'US',
		},
	];
};

export const getChartOptionForYearlyAndWeekly: Function = (
	data,
	type: 'weekly' | 'yearly' | 'monthly',
	highest?,
	lowest?
) => {
	if (type === 'yearly') {
		const h = [highest];
		const l = [lowest];
		return withJeptoLegacyChartSurface({
			title: {
				text: '',
				style: {
					display: 'none',
				},
			},
			subtitle: {
				text: '',
				style: {
					display: 'none',
				},
			},
			plotOptions: {
				series: {
					states: {
						hover: {
							lineWidth: 2,
						},
					},
				},
			},
			credits: {
				enabled: false,
			},
			xAxis: {
				type: 'datetime',
				tickInterval: 30 * 24 * 3600 * 1000, // 1 month
				labels: {
					format: '{value:%b}',
				},
			},
			yAxis: {
				title: {
					text: null,
				},
				allowDecimals: true,
			},
			tooltip: {
				crosshairs: true,
				// split: true,
				// shared: true,
				// xDateFormat: '%b %d'
				formatter() {
					return this.y;
				},
			},
			legend: {
				itemStyle: {
					fontWeight: 'normal',
				},
				itemDistance: 6,
				symbolPadding: 3,
				itemMarginBottom: 10,
				symbolHeight: 8,
				symbolWidth: 8,
			},
			series: [
				{
					name: 'Highest',
					data: h.map(result => [getEpoch(result[0]), result[1]]),
					type: 'scatter',
					color: '#66BB6A',
					tooltip: false,
					zIndex: 2,
					marker: {
						enabled: true,
						symbol: 'url(https://static.jepto.com/image/highest.svg)',
						width: 16,
						height: 16,
					},
					showInLegend: true,
				},
				{
					name: 'Lowest',
					data: l.map(result => [getEpoch(result[0]), result[1]]),
					type: 'scatter',
					color: '#F3403D',
					tooltip: false,
					zIndex: 2,
					marker: {
						enabled: true,
						symbol: 'url(https://static.jepto.com/image/lowest.svg)',
						width: 16,
						height: 16,
					},
					showInLegend: true,
				},
				{
					name: 'Trend',
					data: data.map(result => [getEpoch(result[0]), result[1]]),
					color: '#651FFF',
					dashStyle: 'solid',
					zIndex: 1,
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: true,
				},
				{
					name: 'Target',
					type: 'line',
					color: '#66BB6A',
					dashStyle: 'Dot',
					lineWidth: 2,
					marker: {
						enabled: false,
						symbol: 'circle',
						fillColor: '#FFF',
						lineColor: '#66BB6A',
						lineWidth: 2,
						width: 6,
						height: 6,
						radius: 3,
						states: {
							hover: {
								fillColor: '#FFF',
								radius: 3,
								radiusPlus: 1,
							},
						},
					},
					zIndex: 2,
					data: [],
					showInLegend: false,
				},
				{
					name: 'Prediction',
					type: 'line',
					color: '#651FFF',
					dashStyle: 'Dot',
					lineWidth: 2,
					marker: {
						enabled: false,
						symbol: 'circle',
						fillColor: '#FFF',
						lineColor: '#651FFF',
						lineWidth: 2,
						width: 6,
						height: 6,
						radius: 3,
						states: {
							hover: {
								fillColor: '#FFF',
								radius: 3,
								radiusPlus: 1,
							},
						},
					},
					zIndex: 1,
					data: [],
					showInLegend: false,
				},
				{
					name: 'Daily Performance',
					data: [],
					color: '#42A5F5',
					lineWidth: 2,
					dashStyle: 'solid',
					marker: {
						enabled: false,
						symbol: 'diamond',
					},
					showInLegend: false,
				},
				{
					name: `MTD Performance`,
					data: [],
					zIndex: 2,
					color: '#651FFF',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `WTD Performance`,
					data: [],
					zIndex: 2,
					color: '#651FFF',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `Rolling Avg.`,
					data: [],
					zIndex: 1,
					color: '#651FFF',
					dashStyle: 'dot',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `Prediction`,
					data: [],
					zIndex: 1,
					color: '#42A5F5',
					dashStyle: 'dot',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `MTD Target`,
					data: [],
					color: '#66BB6A',
					zIndex: 0,
					showInLegend: false,
					marker: {
						enabled: false,
						radius: 0,
						symbol: 'square',
					},
				},
				{
					name: `WTD Target`,
					data: [],
					color: '#66BB6A',
					zIndex: 0,
					showInLegend: false,
					marker: {
						enabled: false,
						radius: 0,
						symbol: 'square',
					},
				},
			],
		});
	} else if (type === 'monthly') {
		return withJeptoLegacyChartSurface({
			title: {
				text: '',
				style: {
					display: 'none',
				},
			},
			subtitle: {
				text: '',
				style: {
					display: 'none',
				},
			},
			plotOptions: {
				series: {
					states: {
						hover: {
							lineWidth: 2,
						},
					},
				},
			},
			credits: {
				enabled: false,
			},
			xAxis: {
				tickInterval: 1,
				type: 'category',
				// tickInterval: 3600 * 1000, // 1 month
			},
			yAxis: {
				title: {
					text: null,
				},
				allowDecimals: true,
			},
			tooltip: {
				crosshairs: true,
				// split: true,
				// shared: true,
				// xDateFormat: '%b %d'
				formatter() {
					return this.y;
				},
			},
			legend: {
				itemStyle: {
					fontWeight: 'normal',
				},
				itemDistance: 6,
				symbolPadding: 3,
				itemMarginBottom: 10,
				symbolHeight: 8,
				symbolWidth: 8,
			},
			series: [
				{
					name: 'Highest',
					data: [],
					type: 'scatter',
					color: '#66BB6A',
					tooltip: false,
					zIndex: 2,
					marker: {
						enabled: true,
						symbol: 'url(https://static.jepto.com/image/highest.svg)',
						width: 16,
						height: 16,
					},
					showInLegend: false,
				},
				{
					name: 'Lowest',
					data: [],
					type: 'scatter',
					color: '#F3403D',
					tooltip: false,
					zIndex: 2,
					marker: {
						enabled: true,
						symbol: 'url(https://static.jepto.com/image/lowest.svg)',
						width: 16,
						height: 16,
					},
					showInLegend: false,
				},
				{
					name: 'Trend',
					data: data.map(result => [result[0], result[1]]),
					color: '#651FFF',
					dashStyle: 'solid',
					zIndex: 1,
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: true,
				},
				{
					name: 'Target',
					type: 'line',
					color: '#66BB6A',
					dashStyle: 'Dot',
					lineWidth: 2,
					marker: {
						enabled: false,
						symbol: 'circle',
						fillColor: '#FFF',
						lineColor: '#66BB6A',
						lineWidth: 2,
						width: 6,
						height: 6,
						radius: 3,
						states: {
							hover: {
								fillColor: '#FFF',
								radius: 3,
								radiusPlus: 1,
							},
						},
					},
					zIndex: 2,
					data: [],
					showInLegend: false,
				},
				{
					name: 'Prediction',
					type: 'line',
					color: '#651FFF',
					dashStyle: 'Dot',
					lineWidth: 2,
					marker: {
						enabled: false,
						symbol: 'circle',
						fillColor: '#FFF',
						lineColor: '#651FFF',
						lineWidth: 2,
						width: 6,
						height: 6,
						radius: 3,
						states: {
							hover: {
								fillColor: '#FFF',
								radius: 3,
								radiusPlus: 1,
							},
						},
					},
					zIndex: 1,
					data: [],
					showInLegend: false,
				},
				{
					name: 'Daily Performance',
					data: [],
					color: '#42A5F5',
					lineWidth: 2,
					dashStyle: 'solid',
					marker: {
						enabled: false,
						symbol: 'diamond',
					},
					showInLegend: false,
				},
				{
					name: `MTD Performance`,
					data: [],
					zIndex: 2,
					color: '#651FFF',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `WTD Performance`,
					data: [],
					zIndex: 2,
					color: '#651FFF',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `Rolling Avg.`,
					data: [],
					zIndex: 1,
					color: '#651FFF',
					dashStyle: 'dot',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `Prediction`,
					data: [],
					zIndex: 1,
					color: '#42A5F5',
					dashStyle: 'dot',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `MTD Target`,
					data: [],
					color: '#66BB6A',
					zIndex: 0,
					showInLegend: false,
					marker: {
						enabled: false,
						radius: 0,
						symbol: 'square',
					},
				},
				{
					name: `WTD Target`,
					data: [],
					color: '#66BB6A',
					zIndex: 0,
					showInLegend: false,
					marker: {
						enabled: false,
						radius: 0,
						symbol: 'square',
					},
				},
			],
		});
	} else {
		log('collection clg data', data);
		// let _data = data.map((item) => {
		// 	return item = item.map((res) => {
		// 		res[0] = ""
		// 		if(res[0] === "1")
		// 	})
		// })
		return withJeptoLegacyChartSurface({
			title: {
				text: '',
				style: {
					display: 'none',
				},
			},
			subtitle: {
				text: '',
				style: {
					display: 'none',
				},
			},
			credits: { enabled: false },
			legend: {
				itemStyle: {
					color: 'rgba(15,21,38,0.54)',
					fontWeight: 'normal',
				},
			},
			xAxis: {
				tickInterval: 1,
				type: 'category',
				// type: 'datetime',
				// tickInterval: 24 * 3600 * 1000, // weekly
				// labels: {
				// 	format: '{value:%a}'
				// }
			},
			yAxis: {
				title: { text: null },
			},
			plotOptions: {
				series: {
					label: { connectorAllowed: false },
				},
			},
			tooltip: {
				// crosshairs: false,
				crosshairs: true,
				// split: true,
				// shared: true,
				// pointFormat: '{point.y:,.0f}',
				// borderColor: '#651FFF'
				formatter() {
					return this.y;
				},
			},
			series: [
				{
					name: 'Trend',
					type: 'line',
					color: '#651FFF',
					fillOpacity: 0.1,
					marker: {
						enabled: false,
						symbol: 'line',
					},
					zIndex: 1,
					// data: data.map(item => (item.length === 2) && (item[0], item[1])),
					data: data.map(result => {
						let day = {
							'1': 'Mon',
							'2': 'Tue',
							'3': 'Wed',
							'4': 'Thu',
							'5': 'Fri',
							'6': 'Sat',
							'7': ' Sun',
						};
						let i = day[result[0]];
						return [i, result[1]];
					}),
					_symbolIndex: 0,
					showInLegend: true,
				},
				{
					name: 'Target',
					type: 'line',
					color: '#66BB6A',
					dashStyle: 'Dot',
					lineWidth: 2,
					marker: {
						enabled: false,
						symbol: 'circle',
						fillColor: '#FFF',
						lineColor: '#66BB6A',
						lineWidth: 2,
						width: 6,
						height: 6,
						radius: 3,
						states: {
							hover: {
								fillColor: '#FFF',
								radius: 3,
								radiusPlus: 1,
							},
						},
					},
					zIndex: 2,
					data: [],
					showInLegend: false,
				},
				{
					name: 'Prediction',
					type: 'line',
					color: '#651FFF',
					dashStyle: 'Dot',
					lineWidth: 2,
					marker: {
						enabled: false,
						symbol: 'circle',
						fillColor: '#FFF',
						lineColor: '#651FFF',
						lineWidth: 2,
						width: 6,
						height: 6,
						radius: 3,
						states: {
							hover: {
								fillColor: '#FFF',
								radius: 3,
								radiusPlus: 1,
							},
						},
					},
					zIndex: 1,
					data: [],
					showInLegend: false,
				},
				{
					name: 'Daily Performance',
					data: [],
					color: '#42A5F5',
					lineWidth: 2,
					dashStyle: 'solid',
					marker: {
						enabled: false,
						symbol: 'diamond',
					},
					showInLegend: false,
				},
				{
					name: `MTD Performance`,
					data: [],
					zIndex: 2,
					color: '#651FFF',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `WTD Performance`,
					data: [],
					zIndex: 2,
					color: '#651FFF',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `Rolling Avg.`,
					data: [],
					zIndex: 1,
					color: '#651FFF',
					dashStyle: 'dot',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `Prediction`,
					data: [],
					zIndex: 1,
					color: '#42A5F5',
					dashStyle: 'dot',
					marker: {
						enabled: false,
						symbol: 'circle',
					},
					showInLegend: false,
				},
				{
					name: `MTD Target`,
					data: [],
					color: '#66BB6A',
					zIndex: 0,
					showInLegend: false,
					marker: {
						enabled: false,
						radius: 0,
						symbol: 'square',
					},
				},
				{
					name: `WTD Target`,
					data: [],
					color: '#66BB6A',
					zIndex: 0,
					showInLegend: false,
					marker: {
						enabled: false,
						radius: 0,
						symbol: 'square',
					},
				},
				{
					name: 'Highest',
					data: [],
					type: 'scatter',
					color: '#66BB6A',
					tooltip: false,
					showInLegend: false,
					zIndex: 2,
					marker: {
						enabled: false,
						symbol: 'url(https://static.jepto.com/image/highest.svg)',
					},
				},
				{
					name: 'Lowest',
					data: [],
					type: 'scatter',
					color: '#F3403D',
					showInLegend: false,
					zIndex: 2,
					marker: {
						enabled: false,
						symbol: 'url(https://static.jepto.com/image/lowest.svg)',
					},
				},
			],
		});
	}
};

export const getChartOptionForSeriesAndCumulative: Function = (
	current: Array<any>,
	target: Array<any>,
	prediction: Array<any>,
	currency?: string
) => {
	const newActual: string[][] = [...current];
	const actualLastDay = newActual.pop();
	const newPrediction: string[][] = [...prediction];
	newPrediction.splice(0, 1);
	newPrediction.unshift(actualLastDay);
	return withJeptoLegacyChartSurface({
		title: { text: null },
		credits: { enabled: false },
		subtitle: { text: null },
		legend: {
			itemStyle: { color: 'rgba(15,21,38,0.54)', fontWeight: 'normal' },
		},
		xAxis: {
			type: 'datetime',
			tickInterval: 24 * 3600 * 1000,
			labels: {
				format: '{value:%b %e}',
				style: { color: 'rgba(15,21,38,0.54)' },
			},
		},
		yAxis: {
			title: { text: null },
		},
		plotOptions: {
			series: {
				label: { connectorAllowed: false },
			},
		},
		tooltip: {
			crosshairs: false,
			shared: true,
			pointFormat: '{point.y:,.0f}',
			valuePrefix: Boolean(currency) ? currency : '$',
			borderColor: '#651FFF',
			formatter() {
				let date = Number(new Date(this.x));
				if (this.x === getEpoch(actualLastDay[0])) {
					// Current Date
					let output1 = `<span style=font-size:10px>${Highcharts.dateFormat('%A, %b %e, %Y', date)}</span><br/>`;
					this.points.forEach(point => {
						let y = Boolean(currency)
							? currency === '%'
								? `${point.y}${currency}`
								: `${currency}${point.y}`
							: point.y;
						if (point.series.name == 'Target') {
							output1 += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${y}</b><br/>`;
						} else {
							if (point.series.name == 'Prediction') {
								return;
							} else {
								output1 += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y.toLocaleString()}</b><br/>`;
							}
						}
					});
					return output1;
				} else {
					let output2 = `<span style=font-size:10px>${Highcharts.dateFormat('%A, %b %e, %Y', date)}</span><br/>`;
					if (this.points !== undefined) {
						this.points.forEach(point => {
							let y = Boolean(currency)
								? currency === '%'
									? `${point.y}${currency}`
									: `${currency}${point.y}`
								: point.y;
							if (point.series.name == 'Prediction') {
								output2 += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${y}</b><br/>`;
							} else {
								output2 += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${y}</b><br/>`;
							}
						});
					}
					return output2;
				}
			},
		},
		series: [
			{
				name: 'Actual',
				type: 'area',
				color: '#651FFF',
				fillOpacity: 0.1,
				marker: {
					enabled: false,
					symbol: 'line',
				},
				zIndex: 3,
				data: current.map(
					item =>
						item.length === 2 &&
						getFormattedXYDataObject(item[0], item[1])
				),
				_symbolIndex: 0,
				showInLegend: true,
				connectNulls: true,
			},
			{
				name: 'Target',
				type: 'line',
				color: '#66BB6A',
				dashStyle: 'Dot',
				lineWidth: 2,
				marker: {
					enabled: false,
					symbol: 'circle',
					fillColor: '#FFF',
					lineColor: '#66BB6A',
					lineWidth: 2,
					width: 6,
					height: 6,
					radius: 3,
					states: {
						hover: {
							fillColor: '#FFF',
							radius: 3,
							radiusPlus: 1,
						},
					},
				},
				zIndex: 2,
				data: target.map(
					item =>
						item.length === 2 &&
						getFormattedXYDataObject(item[0], item[1])
				),
				showInLegend: true,
				connectNulls: true,
			},
			{
				name: 'Prediction',
				type: 'line',
				color: '#651FFF',
				dashStyle: 'Dot',
				lineWidth: 2,
				marker: {
					enabled: false,
					symbol: 'circle',
					fillColor: '#FFF',
					lineColor: '#651FFF',
					lineWidth: 2,
					width: 6,
					height: 6,
					radius: 3,
					states: {
						hover: {
							fillColor: '#FFF',
							radius: 3,
							radiusPlus: 1,
						},
					},
				},
				zIndex: 1,
				data: newPrediction.map(
					item =>
						item.length === 2 &&
						getFormattedXYDataObject(item[0], item[1])
				),
				showInLegend: true,
				connectNulls: true,
			},
		],
	});
};

export const getChartOptionKPIAds: Function = (
	dailyPerformance: Array<any>,
	periodToDatePerformance: Array<any>,
	rollingAverage: Array<any>,
	prediction: Array<any>,
	target: Array<any>,
	reportingPeriod: string,
	currency: string
) => {
	let title;
	if (reportingPeriod === 'CUSTOM') title = 'PTD';
	if (reportingPeriod === 'MONTHLY') title = 'MTD';
	if (reportingPeriod === 'WEEKLY') title = 'WTD';
	let lastDay = [...periodToDatePerformance].pop();
	return withJeptoLegacyChartSurface({
		title: {
			text: null,
		},
		credits: {
			enabled: false,
		},
		xAxis: {
			type: 'datetime',
			tickInterval: 24 * 3600 * 1000,
		},
		yAxis: {
			title: {
				text: null,
			},
		},
		tooltip: {
			crosshairs: false,
			shared: true,
			valuePrefix: currency,
			borderColor: '#651FFF',
			formatter() {
				let date = Number(new Date(this.x));
				if (this.x === getEpoch(lastDay[0])) {
					var output1 = `<span style=font-size:10px>${Highcharts.dateFormat('%A, %b %e, %Y', date)}</span><br/>`;
					this.points.forEach(point => {
						let y =
							currency === '%'
								? `${point.y}${currency}`
								: `${currency}${point.y}`;
						if (
							point.series.name == `Prediction` ||
							point.series.name === 'Rolling Avg.'
						) {
							return;
						} else {
							output1 += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${y}</b><br/>`;
						}
					});
					return output1;
				} else {
					var output2 = `<span style=font-size:10px>${Highcharts.dateFormat('%A, %b %e, %Y', date)}</span><br/>`;
					this.points.forEach(point => {
						let y =
							currency === '%'
								? `${point.y}${currency}`
								: `${currency}${point.y}`;
						output2 += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${y}</b><br/>`;
					});
					return output2;
				}
			},
		},
		legend: {},
		series: [
			{
				name: 'Daily Performance',
				data: dailyPerformance.map(item =>
					getFormattedXYDataObject(item[0], item[1])
				),
				zIndex: 1,
				color: '#42A5F5',
				lineWidth: 2,
				dashStyle: 'solid',
				marker: {
					enabled: false,
					symbol: 'diamond',
				},
				showInLegend: true,
			},
			{
				name: `${title} Performance`,
				data: periodToDatePerformance.map(item =>
					getFormattedXYDataObject(item[0], item[1])
				),
				zIndex: 2,
				color: '#651FFF',
				marker: {
					enabled: false,
					symbol: 'circle',
				},
				showInLegend: true,
			},
			{
				name: `Rolling Avg.`,
				data: rollingAverage.map(item =>
					getFormattedXYDataObject(item[0], item[1])
				),
				zIndex: 1,
				color: '#651FFF',
				dashStyle: 'dot',
				marker: {
					enabled: false,
					symbol: 'circle',
				},
				showInLegend: true,
			},
			{
				name: `Prediction`,
				data: prediction.map(item =>
					getFormattedXYDataObject(item[0], item[1])
				),
				zIndex: 1,
				color: '#42A5F5',
				dashStyle: 'dot',
				marker: {
					enabled: false,
					symbol: 'circle',
				},
				showInLegend: true,
			},
			{
				name: `${title} Target`,
				data: target.map(item =>
					getFormattedXYDataObject(item[0], item[1])
				),
				color: '#66BB6A',
				zIndex: 0,
				marker: {
					enabled: false,
					radius: 0,
					symbol: 'square',
				},
				showInLegend: true,
			},
		],
	});
};

import { appendSentryLogDetail, safeStringify } from './safe-stringify';
export { safeStringify } from './safe-stringify';

/** Logs errors to Sentry. No-op if Sentry is not initialized. Logs to console in non-production. */
export const sentryLogError = (
	messageOrError: string | unknown,
	attributes?: Record<string, unknown>
): void => {
	try {
		if (!environment.production) {
			log('sentryLogError', safeStringify(messageOrError));
		}
		if (!Sentry.logger) return;
		const base = safeStringify(messageOrError);
		const attrDetail =
			attributes && Object.keys(attributes).length > 0
				? safeStringify(attributes)
				: '';
		const message = appendSentryLogDetail(base, attrDetail);
		Sentry.logger.error(message, attributes);
	} catch {
		// Swallow to avoid breaking callers
	}
};

/** Logs success events to Sentry. No-op if Sentry is not initialized. Logs to console in non-production. */
export const sentryLogSuccess = (message: string, value?: unknown): void => {
	try {
		if (!environment.production) {
			value != null
				? log('sentryLogSuccess', message, value)
				: log('sentryLogSuccess', message);
		}
		if (!Sentry.logger) return;
		const msg =
			typeof message === 'string' ? message : safeStringify(message);
		Sentry.logger.info(msg, value != null ? { value } : {});
	} catch {
		// Swallow to avoid breaking callers
	}
};

export const DEBUG = (message: string, value: any): void => {
	if (!environment.production) {
		log('collection NON PROD LOGS: ', `${message} : ${value}`);
	} else {
		log('collection Debug: ', `Jepto Success Production ${message}`);
	}
};

/** Logs warnings to Sentry. No-op if Sentry is not initialized. Logs to console in non-production. */
export const sentryLogWarn = (
	message: string,
	attributes?: Record<string, unknown>
): void => {
	try {
		if (!environment.production) {
			log('sentryLogWarn', message, attributes ?? {});
		}
		if (!Sentry.logger) return;
		Sentry.logger.warn(message, attributes);
	} catch {
		// Swallow to avoid breaking callers
	}
};

/** Logs debug events to Sentry. No-op if Sentry is not initialized. Logs to console in non-production. */
export const sentryLogDebug = (
	message: string,
	attributes?: Record<string, unknown>
): void => {
	try {
		if (!environment.production) {
			log('sentryLogDebug', message, attributes ?? {});
		}
		if (!Sentry.logger) return;
		Sentry.logger.debug(message, attributes);
	} catch {
		// Swallow to avoid breaking callers
	}
};

/** Logs events to Sentry. No-op if Sentry is not initialized. Logs to console in non-production. */
export const sentryLogEvent: Function = (
	event: string,
	content?: Record<string, unknown> | null
) => {
	try {
		if (!environment.production) {
			log(
				'sentryLogEvent',
				typeof event === 'string' ? event : safeStringify(event),
				content ?? {}
			);
		}
		if (!Sentry.logger) return;
		const msg = typeof event === 'string' ? event : safeStringify(event);
		const attrs = content && typeof content === 'object' ? content : {};
		Sentry.logger.info(msg, attrs);
	} catch {
		// Swallow to avoid breaking callers
	}
};

export const deleteItemLocally: Function = (
	items: Array<any>,
	detail: Object,
	id: string
): Array<any> => {
	const index = items.findIndex(
		result => result[`${id}`] === detail[`${id}`]
	);
	const newItems = [...items];
	newItems.splice(index, 1);
	return newItems;
};

export const generateArray: Function = (length: number, startWith: number) => {
	return Array.from(Array(length).keys(), (_, i) => {
		i += startWith;
		if (String(i).length == 1) {
			return {
				name: String(i).padStart(2, '0'),
				value: String(i).padStart(2, '0'),
			};
		} else {
			return { name: `${i}`, value: `${i}` };
		}
	});
};
export const renderMasonry: Function = (
	parentElement: string,
	childElement: string
): any => {
	let container = document.querySelector(parentElement);
	if (container) {
		const masonry = new Masonry(container, {
			itemSelector: childElement,
			transitionDuration: '0.2s',
			gutter: 15,
			stagger: 30,
			percentPosition: false,
			resize: false,
			horizontalOrder: true,
		});
		return masonry;
	}
};

// const DATA = Array.from({ length: 20 }, (v, i) => ({
//     id: i + 1,
//     name: `Element #${i + 1}`
// }));

export const getTransformedSelectedTime: Function = (
	time: string
): string[] => {
	const splittedTime = time.split(':');
	let hour = parseFloat(splittedTime[0]);
	if (hour === 0) {
		hour = 12;
	} else if (hour > 12) {
		hour = hour - 12;
	}

	const min = parseFloat(splittedTime[1]);
	const ampm = parseFloat(splittedTime[0]) >= 12 ? 'pm' : 'am';

	return [String(hour).padStart(2, '0'), String(min).padStart(2, '0'), ampm];
};

export const clearJeptoCookies: Function = (): void => {
	var cookies = document.cookie.split('; ');
	for (var c = 0; c < cookies.length; c++) {
		var d = window.location.hostname.split('.');
		while (d.length > 0) {
			var cookieBase =
				encodeURIComponent(cookies[c].split(';')[0].split('=')[0]) +
				'=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=' +
				d.join('.') +
				' ;path=';
			var p = location.pathname.split('/');
			document.cookie = cookieBase + '/';
			while (p.length > 0) {
				document.cookie = cookieBase + p.join('/');
				p.pop();
			}
			d.shift();
		}
	}
	localStorage.removeItem('lau');
};

export const disableDOM: Function = (): void => {
	const body: any = document.getElementsByTagName('body');
	body[0].style = 'pointer-events: none;';
};

export const enableDOM: Function = (): void => {
	const body: any = document.getElementsByTagName('body');
	body[0].style = 'pointer-events: visible;';
};

export const getConnectionServices: Function = (
	clientConnectionData: any,
	googleServices: any[],
	clientDetails?: any
): Array<{
	name: string;
	imgSrc: string;
	data: any;
	isComingSoon: boolean;
	comingSoonMessage: string;
	clientDetails?: any;
	key?: string;
}> => {
	const resultKeys: string[] = Object.keys(clientConnectionData);
	if (!Boolean(googleServices)) return;
	return googleServices.map(item => {
		const serviceName: string = camelCase(
			item.name === 'Google Business Profile'
				? 'Google My Business'
				: item.name
		);
		const googleServiceData: any = pickBy(
			clientConnectionData[serviceName]
		);
		if (
			includes(resultKeys, serviceName) &&
			!isEmpty(googleServiceData) &&
			Boolean(clientConnectionData.connectionStatus) &&
			!item.isComingSoon
		) {
			const connectionStatus: string =
				clientConnectionData.connectionStatus[serviceName] &&
				clientConnectionData.connectionStatus[serviceName].status
					? clientConnectionData.connectionStatus[serviceName].status
					: '';
			if (Boolean(clientDetails)) {
				return {
					...item,
					isComingSoon: false,
					data: {
						...googleServiceData,
						sortKey: clientConnectionData.sortKey,
						connectionStatus,
					},
					key: serviceName,
					clientDetails: clientDetails,
				};
			} else {
				return {
					...item,
					isComingSoon: false,
					data: {
						...googleServiceData,
						sortKey: clientConnectionData.sortKey,
						connectionStatus,
					},
					key: serviceName,
				};
			}
		}
		return { ...item, data: null };
	});
};

export const getTwoAxisCharts: Function = (data1, data2) => {
	return withJeptoLegacyChartSurface({
		chart: {
			backgroundColor: '#ffffff',
			height: 200,
			events: {
				render: onLegacyChartRender(1000),
				load: function () {
					log('collection clg getTwoAxisCharts', this);
					onLegacyChartLoad(0).call(this);
				},
			},
		},
		title: { text: null },
		credits: { enabled: false },
		subtitle: { text: null },
		legend: {
			itemStyle: { color: 'rgba(15,21,38,0.54)', fontWeight: 'normal' },
		},
		xAxis: {
			type: 'datetime',
			labels: {
				format: '{value:%b %e}',
				style: { color: 'rgba(15,21,38,0.54)' },
			},
			lineWidth: 0,
			minorGridLineWidth: 0,
			minorTickLength: 0,
			tickLength: 0,
		},
		yAxis: { title: { text: null } },
		plotOptions: { series: { label: { connectorAllowed: false } } },
		tooltip: {
			formatter: function () {
				const date = Highcharts.dateFormat('%A, %b %e, %Y', this.x);
				var tooltip = `<span style=font-size:10px>${date}</span><br/>`;
				this.points.forEach(point => {
					if (point.series.name === 'CTR') {
						tooltip += `<span style="color:${point.color};">●</span><span style="font-weight:normal;"> ${point.series.name}:</span><b>${point.y}%</br>`;
					} else {
						tooltip += `<span style="color:${point.color};">●</span> ${point.series.name}: <b>${point.y}</br>`;
					}
				});
				return tooltip;
			},
			shared: true,
		},
		series: [
			{
				name: 'Maps',
				color: '#651FFF',
				lineWidth: 2,
				dashStyle: 'solid',
				marker: { enabled: false, symbol: 'circle' },
				data: data1,
			},
			{
				name: 'Search',
				color: '#42A5F5',
				lineWidth: 2,
				dashStyle: 'solid',
				marker: { enabled: false, symbol: 'circle' },
				data: data2,
			},
		],
	});
};

export const getThreeAxisCharts: Function = (
	data1,
	data2,
	data3,
	title1,
	title2,
	title3
) => {
	return withJeptoLegacyChartSurface({
		chart: { backgroundColor: '#ffffff' },
		title: { text: null },
		credits: { enabled: false },
		subtitle: { text: null },
		legend: {
			itemStyle: { color: 'rgba(15,21,38,0.54)', fontWeight: 'normal' },
		},
		xAxis: {
			type: 'datetime',
			labels: {
				format: '{value:%b %e}',
				style: { color: 'rgba(15,21,38,0.54)' },
			},
			lineWidth: 0,
			minorGridLineWidth: 0,
			minorTickLength: 0,
			tickLength: 0,
		},
		yAxis: [
			{
				title: { text: null },
			},
		],
		plotOptions: { series: { label: { connectorAllowed: false } } },
		tooltip: {
			formatter: function () {
				const date = Highcharts.dateFormat('%A, %b %e, %Y', this.x);
				var tooltip = `<span style=font-size:10px>${date}</span><br/>`;
				this.points.forEach(point => {
					tooltip += `<b><span style="color:${point.color};">●</span> ${point.series.name}: ${point.y}</b></br>`;
				});
				return tooltip;
			},
			shared: true,
		},
		series: [
			{
				name: title1,
				color: '#651FFF',
				lineWidth: 2,
				dashStyle: 'solid',
				marker: { enabled: false, symbol: 'circle' },
				data: data1,
			},
			{
				name: title2,
				color: '#42A5F5',
				lineWidth: 2,
				dashStyle: 'solid',
				marker: { enabled: false, symbol: 'circle' },
				data: data2,
			},
			{
				name: title3,
				color: '#66BB6A',
				lineWidth: 2,
				dashStyle: 'solid',
				marker: { enabled: false, symbol: 'circle' },
				data: data3,
			},
		],
	});
};

export const getBarAndLineCharts: Function = (
	barChartData,
	lineChartData,
	showMixMaxYAxis: boolean = false
) => {
	return {
		chart: {
			height: 400,
			backgroundColor: '#ffffff',
		},
		title: {
			text: null,
		},
		credits: {
			enabled: false,
		},
		subtitle: {
			text: null,
		},
		legend: {
			itemStyle: {
				color: 'rgba(15,21,38,0.54)',
				fontWeight: 'normal',
			},
		},
		xAxis: {
			type: 'datetime',
			labels: {
				format: '{value:%b %e}',
				style: {
					color: 'rgba(15,21,38,0.54)',
				},
			},
			lineWidth: 0,
			minorGridLineWidth: 0,
			minorTickLength: 0,
			tickLength: 0,
		},
		yAxis: [
			{
				title: {
					text: 'Reviews',
				},
				labels: {
					format: '{value}',
					formatter: function () {
						if (!showMixMaxYAxis) {
							return this.value;
						}
						if (
							this.value === this.axis.min ||
							this.value === this.axis.max
						) {
							return this.value;
						}
						return '';
					},
				},
				gridLineWidth: 0, // Remove horizontal grid lines
				tickInterval: 1, // Set the tick interval to whole numbers
			},
			{
				title: {
					text: 'Rating',
				},
				min: 0,
				max: 5, // Set the maximum value for the Average Rating yAxis
				labels: {
					format: '{value}',
				},
				opposite: true,
				tickInterval: 1,
			},
		],
		plotOptions: {
			series: {
				label: {
					connectorAllowed: false,
				},
			},
		},
		tooltip: {
			formatter: function () {
				const date = Highcharts.dateFormat('%A, %b %e, %Y', this.x);
				var tooltip = `<span style=font-size:10px>${date}</span><br/>`;
				this.points.forEach(point => {
					if (point.series.name === 'Average Rating ') {
						tooltip += `<span style="color:${point.color};">●</span><span style="font-weight:normal;"> ${point.series.name}:</span><b>${point.y}</b><br>`;
					} else {
						tooltip += `<span style="color:${point.color};">●</span> ${point.series.name}: <b>${point.y}</b><br>`;
					}
				});
				return tooltip;
			},
			shared: true,
		},
		series: [
			{
				name: 'Number of Reviews',
				color: '#42A5F5',
				type: 'column', // Set column chart type
				yAxis: 0,
				marker: {
					enabled: false,
				},
				pointWidth: 4, // Adjust the width of the columns
				data: barChartData,
			},
			{
				name: 'Average Rating',
				color: '#651FFF',
				lineWidth: 2,
				dashStyle: 'solid',
				yAxis: 1,
				marker: {
					enabled: false,
				},
				data: lineChartData,
			},
		],
	};
};

export const getMonths: Function = () => {
	return [
		{ name: 'Jan', value: 1 },
		{ name: 'Feb', value: 2 },
		{ name: 'Mar', value: 3 },
		{ name: 'Apr', value: 4 },
		{ name: 'May', value: 5 },
		{ name: 'Jun', value: 6 },
		{ name: 'Jul', value: 7 },
		{ name: 'Aug', value: 8 },
		{ name: 'Sep', value: 9 },
		{ name: 'Oct', value: 10 },
		{ name: 'Nov', value: 11 },
		{ name: 'Dec', value: 12 },
	];
};

export const getDateOptions: Function = () => {
	return [
		{ name: '01', value: 1 },
		{ name: '02', value: 2 },
		{ name: '03', value: 3 },
		{ name: '04', value: 4 },
		{ name: '05', value: 5 },
		{ name: '06', value: 6 },
		{ name: '07', value: 7 },
		{ name: '08', value: 8 },
		{ name: '09', value: 9 },
		{ name: '10', value: 10 },
		{ name: '11', value: 11 },
		{ name: '12', value: 12 },
		{ name: '13', value: 13 },
		{ name: '14', value: 14 },
		{ name: '15', value: 15 },
		{ name: '16', value: 16 },
		{ name: '17', value: 17 },
		{ name: '18', value: 18 },
		{ name: '19', value: 19 },
		{ name: '20', value: 20 },
		{ name: '21', value: 21 },
		{ name: '22', value: 22 },
		{ name: '23', value: 23 },
		{ name: '24', value: 24 },
		{ name: '25', value: 25 },
		{ name: '26', value: 26 },
		{ name: '27', value: 27 },
		{ name: '28', value: 28 },
		{ name: '29', value: 29 },
		{ name: '30', value: 30 },
		{ name: '31', value: 31 },
	];
};

export const convertBase64ToBlob: Function = (base64Image: string): Blob => {
	const parts = base64Image.split(';base64,');
	const imageType = parts[0].split(':')[1];
	const decodedData = window.atob(parts[1]);
	const uInt8Array = new Uint8Array(decodedData.length);
	for (let i = 0; i < decodedData.length; ++i) {
		uInt8Array[i] = decodedData.charCodeAt(i);
	}
	return new Blob([uInt8Array], { type: imageType });
};

export const urlExists: Function = (url, callback): any => {
	var xhr = new XMLHttpRequest();
	xhr.onreadystatechange = function () {
		if (xhr.readyState === 4) {
			callback(xhr.status < 400);
		}
	};
	xhr.open('HEAD', url);
	xhr.send();
};

export const getTimeOptions: Function = (): Array<{
	name: string;
	value: string;
}> => {
	return [
		{ name: '12:00AM', value: '12:00AM' },
		{ name: '12:30AM', value: '12:30AM' },
		{ name: '1:00AM', value: '1:00AM' },
		{ name: '1:30AM', value: '1:30AM' },
		{ name: '2:00AM', value: '2:00AM' },
		{ name: '2:30AM', value: '2:30AM' },
		{ name: '3:00AM', value: '3:00AM' },
		{ name: '3:30AM', value: '3:30AM' },
		{ name: '4:00AM', value: '4:00AM' },
		{ name: '4:30AM', value: '4:30AM' },
		{ name: '5:00AM', value: '5:00AM' },
		{ name: '5:30AM', value: '5:30AM' },
		{ name: '6:00AM', value: '6:00AM' },
		{ name: '6:30AM', value: '6:30AM' },
		{ name: '7:00AM', value: '7:00AM' },
		{ name: '7:30AM', value: '7:30AM' },
		{ name: '8:00AM', value: '8:00AM' },
		{ name: '8:30AM', value: '8:30AM' },
		{ name: '9:00AM', value: '9:00AM' },
		{ name: '9:30AM', value: '9:30AM' },
		{ name: '10:00AM', value: '10:00AM' },
		{ name: '10:30AM', value: '10:30AM' },
		{ name: '11:00AM', value: '11:00AM' },
		{ name: '11:30AM', value: '11:30AM' },
		{ name: '12:00PM', value: '12:00PM' },
		{ name: '12:30PM', value: '12:30PM' },
		{ name: '1:00PM', value: '1:00PM' },
		{ name: '1:30PM', value: '1:30PM' },
		{ name: '2:00PM', value: '2:00PM' },
		{ name: '2:30PM', value: '2:30PM' },
		{ name: '3:00PM', value: '3:00PM' },
		{ name: '3:30PM', value: '3:30PM' },
		{ name: '4:00PM', value: '4:00PM' },
		{ name: '4:30PM', value: '4:30PM' },
		{ name: '5:00PM', value: '5:00PM' },
		{ name: '5:30PM', value: '5:30PM' },
		{ name: '6:00PM', value: '6:00PM' },
		{ name: '6:30PM', value: '6:30PM' },
		{ name: '7:00PM', value: '7:00PM' },
		{ name: '7:30PM', value: '7:30PM' },
		{ name: '8:00PM', value: '8:00PM' },
		{ name: '8:30PM', value: '8:30PM' },
		{ name: '9:00PM', value: '9:00PM' },
		{ name: '9:30PM', value: '9:30PM' },
		{ name: '10:00PM', value: '10:00PM' },
		{ name: '10:30PM', value: '10:30PM' },
		{ name: '11:00PM', value: '11:00PM' },
		{ name: '11:30PM', value: '11:30PM' },
	];
};

export const generateCSV: Function = (rows: any[], opt: any) => {
	const csvConfig = mkConfig(opt);
	const csv = generateCsv(csvConfig)(rows);
	download(csvConfig)(csv);
};
